// web/src/features/admin/users/UserDetail.tsx
//
// Admin "click through a user" view. Surfaces profile, membership, family
// members, and meal plans for a single user, with admin-only meal plan deletes
// (audited via admin_audit_log on the backend). Modeled on CreatorDetail's
// layout for visual consistency with the rest of the admin panel.
//
// Soft-delete is a deliberate TODO — see comment on `confirmDelete` below.
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  Restaurant as RestaurantIcon,
  Cake as ChildIcon,
} from '@mui/icons-material';
import {
  getUser,
  getUserFamilyMembers,
  getUserMealPlans,
  adminDeleteMealPlan,
  AdminUser,
  AdminFamilyMember,
  AdminMealPlanSummary,
} from '../adminApi';

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const membershipColor = (status: string | null | undefined): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  if (!status) return 'default';
  if (status === 'paid') return 'success';
  if (status === 'free') return 'info';
  if (status === 'lapsed') return 'warning';
  if (status === 'cancelled') return 'error';
  if (status === 'onboarding') return 'warning';
  return 'default';
};

const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const userId = parseInt(id || '0', 10);

  const [user, setUser] = useState<AdminUser | null>(null);
  const [members, setMembers] = useState<AdminFamilyMember[]>([]);
  const [plans, setPlans] = useState<AdminMealPlanSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<AdminMealPlanSummary | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [u, m, p] = await Promise.all([
        getUser(userId),
        getUserFamilyMembers(userId),
        getUserMealPlans(userId),
      ]);
      setUser(u);
      setMembers(m);
      setPlans(p);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load user');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) load();
  }, [userId, load]);

  // Admin meal-plan delete. Hard delete for now; audit log captures the
  // snapshot so we can answer "why is this gone" questions.
  // TODO: switch to soft delete once mealplans has a `deleted_at` column —
  // see ROADMAP.md "Admin meal plan soft-delete".
  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await adminDeleteMealPlan(deleteConfirm.plan_id);
      setPlans(prev => prev.filter(p => p.plan_id !== deleteConfirm.plan_id));
      setDeleteConfirm(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to delete plan');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !user) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/users')} sx={{ mb: 2 }}>
          Back to users
        </Button>
        <Alert severity="error">{error || 'User not found'}</Alert>
      </Box>
    );
  }

  const membership = (user as any).membership;

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/users')} sx={{ mb: 2 }}>
        Back to users
      </Button>

      <Typography variant="h4" fontWeight={600} gutterBottom>
        {user.email}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
        <Chip label={`ID ${user.user_id}`} size="small" />
        <Chip
          label={user.role}
          size="small"
          color={user.role === 'admin' || user.role === 'super-admin' ? 'primary' : 'default'}
        />
        <Chip
          label={user.profile_complete ? 'Profile complete' : 'Profile incomplete'}
          size="small"
          color={user.profile_complete ? 'success' : 'warning'}
        />
      </Stack>

      {/* Profile */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Profile</Typography>
        <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
          <Box>
            <Typography variant="caption" color="text.secondary">Created</Typography>
            <Typography variant="body2">{formatDateTime(user.created_at)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Family members</Typography>
            <Typography variant="body2">{user.stats?.familyMemberCount ?? members.length}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Meal plans</Typography>
            <Typography variant="body2">{user.stats?.mealPlanCount ?? plans.length}</Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Membership */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Membership</Typography>
        {membership ? (
          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
            <Box>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label={membership.status}
                  size="small"
                  color={membershipColor(membership.status)}
                />
              </Box>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Plan</Typography>
              <Typography variant="body2">{membership.membership_name || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Auto renew</Typography>
              <Typography variant="body2">
                {membership.auto_renew === null || membership.auto_renew === undefined
                  ? '—'
                  : membership.auto_renew ? 'Yes' : 'No'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Trial end</Typography>
              <Typography variant="body2">{formatDate(membership.trial_end_date)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">End date</Typography>
              <Typography variant="body2">{formatDate(membership.end_date)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Funnel step</Typography>
              <Typography variant="body2">{(membership.funnel_step as string) || '—'}</Typography>
            </Box>
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">No membership row.</Typography>
        )}
      </Paper>

      {/* Family Members */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Family Members ({members.length})
        </Typography>
        {members.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No family members.</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Restrictions</TableCell>
                  <TableCell>Medical</TableCell>
                  <TableCell>Allergies</TableCell>
                  <TableCell>Dislikes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map(m => {
                  const restriction = m.dietary_tags.filter(t => t.type === 'restriction');
                  const medical = m.dietary_tags.filter(t => t.type === 'medical');
                  const allergy = m.dietary_tags.filter(t => t.type === 'allergy');
                  return (
                    <TableRow key={m.member_id}>
                      <TableCell>{m.name}</TableCell>
                      <TableCell>
                        {m.is_child ? (
                          <Chip icon={<ChildIcon />} label="Child" size="small" color="warning" />
                        ) : (
                          <Chip label="Adult" size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        {restriction.length === 0
                          ? '—'
                          : restriction.map(t => t.tag_name).join(', ')}
                      </TableCell>
                      <TableCell>
                        {medical.length === 0 ? '—' : medical.map(t => t.tag_name).join(', ')}
                      </TableCell>
                      <TableCell>
                        {allergy.length === 0 ? '—' : allergy.map(t => t.tag_name).join(', ')}
                      </TableCell>
                      <TableCell>
                        {m.ingredient_dislikes.length === 0
                          ? '—'
                          : m.ingredient_dislikes.join(', ')}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Meal Plans */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Meal Plans ({plans.length})
        </Typography>
        {plans.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No meal plans.</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Plan ID</TableCell>
                  <TableCell>Week of</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Meals</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plans.map(p => (
                  <TableRow key={p.plan_id}>
                    <TableCell>{p.plan_id}</TableCell>
                    <TableCell>{formatDate(p.week_start_date)}</TableCell>
                    <TableCell>
                      <Chip label={p.status || 'unknown'} size="small" />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <RestaurantIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        <span>{p.meal_count}</span>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => setDeleteConfirm(p)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" color="text.secondary">
          Admin deletes are hard deletes (audited in admin_audit_log). Soft-delete is on the roadmap.
        </Typography>
      </Paper>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => !deleting && setDeleteConfirm(null)}>
        <DialogTitle>Delete meal plan?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete plan <strong>#{deleteConfirm?.plan_id}</strong>{' '}
            (week of {formatDate(deleteConfirm?.week_start_date)}) and its meals and shopping list.
            This action is audited but cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete plan'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserDetail;
